import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register, login, saveToken } from '../../services/adminApi';
import logoCircle from '../../assets/logo-circle.jpeg';

const AdminRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // 1. Call registration endpoint
      await register(email, password);
      
      // 2. Automatically log them in on success
      const token = await login(email, password);
      saveToken(token);
      
      // 3. Redirect to admin dashboard
      navigate('/admin');
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginCard}>
        <div style={styles.logoRow}>
          <img src={logoCircle} alt="CRM Logo" style={styles.logoImage} />
          <span style={styles.logoText}>Christ Reigns Admin</span>
        </div>
        <h1 style={styles.loginTitle}>Register Admin</h1>
        <p style={styles.loginSub}>Create a new administrator account</p>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="admin@christreignslab.com"
            required
            autoFocus
          />

          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />

          <label style={styles.label}>Confirm Password</label>
          <input
            style={styles.input}
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            required
          />

          {error && <p style={styles.errorMsg}>{error}</p>}

          <button style={loading ? styles.btnDisabled : styles.btn} type="submit" disabled={loading}>
            {loading ? 'Registering…' : 'Register & Sign In'}
          </button>
        </form>

        <div style={styles.footerLinkWrap}>
          <span style={styles.footerText}>Already have an account? </span>
          <Link to="/admin" style={styles.link}>Sign In</Link>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  loginWrap: { 
    minHeight: '100vh', 
    background: '#f9fafb', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    fontFamily: 'Inter, system-ui, sans-serif' 
  },
  loginCard: { 
    background: '#fff', 
    borderRadius: 16, 
    border: '1px solid #e5e7eb', 
    padding: '40px 36px', 
    width: 380, 
    boxShadow: '0 4px 24px rgba(0,0,0,0.07)' 
  },
  logoRow: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: 12 
  },
  logoImage: { 
    width: 48, 
    height: 48, 
    borderRadius: '50%', 
    objectFit: 'cover' 
  },
  logoText: { 
    color: '#111827', 
    fontWeight: 700, 
    fontSize: 16, 
    letterSpacing: '-0.3px' 
  },
  loginTitle: { 
    fontSize: 24, 
    fontWeight: 700, 
    color: '#111827', 
    margin: '24px 0 6px' 
  },
  loginSub: { 
    color: '#6b7280', 
    fontSize: 14, 
    marginBottom: 24, 
    marginTop: 0 
  },
  form: { 
    display: 'flex', 
    flexDirection: 'column' 
  },
  label: { 
    display: 'block', 
    fontSize: 13, 
    fontWeight: 600, 
    color: '#374151', 
    marginBottom: 6, 
    marginTop: 16 
  },
  input: { 
    width: '100%', 
    padding: '10px 14px', 
    border: '1px solid #d1d5db', 
    borderRadius: 8, 
    fontSize: 14, 
    color: '#111827', 
    outline: 'none', 
    boxSizing: 'border-box', 
    background: '#fff' 
  },
  btn: { 
    background: '#05a5b7', 
    color: '#fff', 
    border: 'none', 
    padding: '11px 20px', 
    borderRadius: 8, 
    cursor: 'pointer', 
    fontWeight: 600, 
    fontSize: 14, 
    marginTop: 24,
    transition: 'background 0.2s'
  },
  btnDisabled: { 
    background: '#9ca3af', 
    color: '#fff', 
    border: 'none', 
    padding: '11px 20px', 
    borderRadius: 8, 
    cursor: 'not-allowed', 
    fontWeight: 600, 
    fontSize: 14, 
    marginTop: 24 
  },
  errorMsg: { 
    color: '#dc2626', 
    fontSize: 13, 
    marginTop: 12, 
    marginBottom: 0 
  },
  footerLinkWrap: {
    marginTop: 20,
    textAlign: 'center',
    fontSize: 13
  },
  footerText: {
    color: '#6b7280'
  },
  link: {
    color: '#05a5b7',
    fontWeight: 600,
    textDecoration: 'none'
  }
};

export default AdminRegisterPage;
